import {
  PreciosImportPreviewRow,
  aplicarEdicionCostoArticulo,
  aplicarEdicionCostoProveedor,
  aplicarEdicionPrecioNetoArticulo,
} from './buildPreciosImportPreview';

/** Par de columnas: valor actual en el sistema + valor nuevo (desde Excel o estimado). */
export type PreciosImportPreviewColumnTipo = 'par' | 'solo-nuevo';

export interface PreciosImportPreviewColumnDef {
  id: string;
  /** Etiqueta corta del concepto (costo, precio neto, etc.). */
  label: string;
  labelActual: string;
  labelNuevo: string;
  /** Letra de columna Excel; si está presente, el valor nuevo se puede editar y persistir al libro. */
  columnaExcel?: string;
  tipo: PreciosImportPreviewColumnTipo;
  /** Solo true para campos mapeados desde Excel (origen importable). */
  editable: boolean;
  destacado?: boolean;
  getActual: (row: PreciosImportPreviewRow) => number | null | undefined;
  getNuevo: (row: PreciosImportPreviewRow) => number | null | undefined;
  setNuevo: (row: PreciosImportPreviewRow, value: number | null) => PreciosImportPreviewRow;
}

export interface BuildPreviewColumnDefsArticuloParams {
  columnaCosto?: string;
  columnaPrecioNeto?: string;
  columnaPrecioMinimo?: string;
}

export interface BuildPreviewColumnDefsProveedorParams {
  columnaCosto: string;
}

/** Columnas de preview para importación por artículo según columnas Excel configuradas. */
export function buildPreviewColumnDefsArticulo(
  params: BuildPreviewColumnDefsArticuloParams
): PreciosImportPreviewColumnDef[] {
  const colCosto = params.columnaCosto?.trim();
  const colPrecio = params.columnaPrecioNeto?.trim();
  const colPrecioMin = params.columnaPrecioMinimo?.trim();
  const columnas: PreciosImportPreviewColumnDef[] = [];

  if (colCosto) {
    columnas.push({
      id: 'costo',
      label: 'Costo base',
      labelActual: 'Costo · actual',
      labelNuevo: `Costo · Excel (${colCosto})`,
      columnaExcel: colCosto,
      tipo: 'par',
      editable: true,
      getActual: (r) => r.costoActual,
      getNuevo: (r) => r.costoNuevo,
      setNuevo: (r, v) => aplicarEdicionCostoArticulo(r, v),
    });
  }

  if (colCosto || colPrecio) {
    columnas.push({
      id: 'precioNeto',
      label: 'Precio neto',
      labelActual: 'P. neto · actual',
      labelNuevo: colPrecio ? `P. neto · Excel (${colPrecio})` : 'P. neto · nuevo',
      columnaExcel: colPrecio,
      tipo: 'par',
      editable: !!colPrecio,
      destacado: true,
      getActual: (r) => r.precioNetoActual,
      getNuevo: (r) => r.precioNetoNuevo,
      setNuevo: (r, v) => aplicarEdicionPrecioNetoArticulo(r, v),
    });

    columnas.push({
      id: 'precioFinal',
      label: 'Precio final',
      labelActual: 'P. final · actual',
      labelNuevo: 'P. final · nuevo',
      tipo: 'par',
      editable: false,
      destacado: true,
      getActual: (r) => r.precioFinalActual,
      getNuevo: (r) => r.precioFinalNuevo,
      setNuevo: (r) => r,
    });
  }

  if (colPrecioMin) {
    columnas.push({
      id: 'precioMinimo',
      label: 'Precio mín. neto',
      labelActual: 'P. mín. · actual',
      labelNuevo: `P. mín. · Excel (${colPrecioMin})`,
      columnaExcel: colPrecioMin,
      tipo: 'par',
      editable: true,
      getActual: (r) => r.precioMinimoActual,
      getNuevo: (r) => r.precioMinimoNuevo,
      setNuevo: (r, v) => ({
        ...r,
        precioMinimoNuevo: v,
        precioMinimoNuevoOriginal: r.precioMinimoNuevoOriginal ?? r.precioMinimoNuevo ?? null,
        editado: true,
      }),
    });
  }

  return columnas;
}

/** Columnas de preview para importación por proveedor (costo desde Excel). */
export function buildPreviewColumnDefsProveedor(
  params: BuildPreviewColumnDefsProveedorParams
): PreciosImportPreviewColumnDef[] {
  const colCosto = params.columnaCosto.trim();
  if (!colCosto) return [];

  return [
    {
      id: 'costo',
      label: 'Costo base',
      labelActual: 'Costo · actual',
      labelNuevo: `Costo · Excel (${colCosto})`,
      columnaExcel: colCosto,
      tipo: 'solo-nuevo',
      editable: true,
      destacado: true,
      getActual: () => null,
      getNuevo: (r) => r.costoNuevo,
      setNuevo: (r, v) => aplicarEdicionCostoProveedor(r, v),
    },
  ];
}

/** Deriva letras de columna Excel para escribir ediciones en el libro. */
export function columnDefsToExcelColumnMap(columnas: PreciosImportPreviewColumnDef[]): {
  columnaCosto?: string;
  columnaPrecioNeto?: string;
  columnaPrecioMinimo?: string;
} {
  const costo = columnas.find((c) => c.id === 'costo' && c.columnaExcel);
  const precioNeto = columnas.find((c) => c.id === 'precioNeto' && c.columnaExcel);
  const precioMinimo = columnas.find((c) => c.id === 'precioMinimo' && c.columnaExcel);
  return {
    ...(costo?.columnaExcel ? { columnaCosto: costo.columnaExcel } : {}),
    ...(precioNeto?.columnaExcel ? { columnaPrecioNeto: precioNeto.columnaExcel } : {}),
    ...(precioMinimo?.columnaExcel ? { columnaPrecioMinimo: precioMinimo.columnaExcel } : {}),
  };
}
